import { Component } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { Route, Navigate, Routes } from 'react-router-dom'
import { navigate } from 'builder/modules/navigate'
import { i18n as I18n } from 'builder/utils/i18n'
import queryString from 'query-string'
import getByPath from 'lodash/get'
import { RouteTransitionSwitch } from 'builder/components/RouteTransitionSwitch'
import { actions } from 'builder/modules/signUp'
import { trackMarketingEvent, trackInternalEvent } from '@rio/tracking'

import {
  Layout,
  SocialProfile,
  Introduction,
  ContactInfo,
  SignUpStepManager,
  Illustrations,
  STEPS,
  TemplatesV2,
} from 'builder/components/SignUp'
import { ConfigScopesEnum, selectors as appSelectors } from 'builder/modules/init'
import { withRouter } from 'builder/hocs/withRouter'
import { DocumentTypes } from 'builder/modules/constants'
import { filterGreekTemplates } from 'builder/utils/filterGreekTemplates'
import { ATS_TEMPLATES, JAPANESE_TEMPLATES } from 'builder/components/Helper/constants'
import { GuestResumeContext } from 'builder/context/GuestResumeContext'
import CookieV4 from 'builder/components/CookieV4'
import { getRecaptchaLoginActionToken } from 'builder/utils/recapchaActions'
import ErrorLogger from 'builder/services/ErrorLogger'
import SingleSignup from 'builder/components/SignUp/SingleSignup'


//
// change templates guest instead if it is as intended to be used instead of this one.
//

class SignUpView extends Component {
  static propTypes = {
    resumeTemplates: PropTypes.array.isRequired,
    coverLetterTemplates: PropTypes.array.isRequired,
    heroTemplateName: PropTypes.string,
    signUp: PropTypes.func.isRequired,
    location: PropTypes.object,
    persistSignUpInfo: PropTypes.func,
    loadSignUpInfo: PropTypes.func,
    getSignUpPayload: PropTypes.func,
    signUpInfo: PropTypes.object,
    config: PropTypes.object.isRequired,
    documentType: PropTypes.oneOf(Object.values(DocumentTypes)),
    locale: PropTypes.string,
    resumeTemplateCategories: PropTypes.array.isRequired,
    coverLetterTemplateCategories: PropTypes.array.isRequired,
  }

  static contextType = GuestResumeContext

  constructor(props) {
    super(props)

    // Preserve initial query parameters
    this.urlParams = queryString.parse(this.props.location.search)
    this.regionPrefix = this.urlParams.current_region_prefix || ''

    // Read social authorization data from the config passed from the backend
    const socialProfile = getByPath(props, 'config.socialProfile')
    props.loadSignUpInfo({ type: this.props.documentType, socialProfile })

    this.singlePageSignUp = this.props.config.features.singlePageSignUp
    this.rtpAtpUnhideCtaAB = this.props.config.features.rtpAtpUnhideCta
    this.atpStickyCategories = this.props.config.features.atpStickyCategories

    this.settings = this.props.config.settings || {}
    // step maker
    let steps =
      (['v1', 'v2'].includes(this.singlePageSignUp) || this.settings.whitelabeled) && [STEPS.templates, STEPS.onePageSignUp] ||
      undefined

    // Config funnel steps
    this.stepManager = new SignUpStepManager({ steps })

    // Initial sign up flow state
    this.state = {
      location: props.location,
      movingDirection: 'forward',
      introductionErrors: {
        firstName: '',
        lastName: '',
      },
      contactInfoErrors: {
        email: '',
      },
      componentWasMounted: false,
    }

    trackMarketingEvent('Sign up', 'Start Funnel')
  }

  navigateWithScope = (url, navigateOptions) => {
    const documentType =
      this.props.documentType === DocumentTypes.coverLetter ? 'cover-letter' : 'resume'
    navigate(`/create-${documentType}/${url}`, navigateOptions)
  }

  componentDidMount() {
    this.props.getSignUpPayload()
    const currentStepName = this.stepManager.getStepByUrl(window.location.pathname)
    const socialProfile = getByPath(this.props, 'config.socialProfile')

    if (this.urlParams.template) {
      const template = this.urlParams.template || this.props.heroTemplateName

      const ignoreBuilderSignup = !!this.urlParams.example_id

      this.handleChooseTemplate(template, { replace: true }, ignoreBuilderSignup)
    }

    if (socialProfile && currentStepName !== STEPS.introduction) {
      this.navigateWithScope(STEPS.introduction)
    }

    this.setState({ componentWasMounted: true })

    const container = document.getElementById('builder-application')
    this.atpLayoutVariant = container?.dataset?.atp_layout ?? 'v0'
  }

  static getDerivedStateFromProps(props, state) {
    if (props.location !== state.location) {
      const staticStepManager = new SignUpStepManager()
      const nextStep = staticStepManager.getStepByUrl(props.location.pathname)
      const currentStep = staticStepManager.getStepByUrl(state.location.pathname)
      const direction = staticStepManager.getDirection(currentStep, nextStep)
      let movingDirection = direction

      return {
        location: props.location,
        movingDirection,
      }
    }

    return null
  }

  get currentNavbarIndex() {
    // TODO: it makes 0 sense to set steps on the start of the function and then hard code
    // here the infoSteps just because why not.
    // it ruins the dynamic part of setting it and controlling it properly.
    const url = this.props.location.pathname
    const currentStep = this.stepManager.getStepByUrl(url)
    const infoSteps = [STEPS.socialProfile, STEPS.introduction, STEPS.contactInfo]

    // 1 and 0 are indices, not boolean
    if (infoSteps.includes(currentStep)) {
      return 1
    }

    return 0
  }

  handleSignUpForwardRedirectUrl = () => {
    const redirectUrl = this.urlParams.redirectUrl
    if (redirectUrl) return redirectUrl

    return this.singlePageSignUp === 'v2' ? '/app/user-favourites' : ''
  }

  handleChooseTemplate = (template, navigateOptions, ignoreBuilderSignup = false) => {
    this.props.persistSignUpInfo({ type: this.props.documentType, template })

    const hasAbTest = this.checkIfAbTestExistsOnTemplates()
    if (hasAbTest) return

    if (
      this.props.config?.features.rbpStepSignup &&
      !ignoreBuilderSignup &&
      this.props.documentType === DocumentTypes.resume
    ) {
      this.context.resetResume({ template })
      navigate(`/guest-builder`)
    } else {
      this.moveToNextPageOrFinish(STEPS.templates, navigateOptions)
    }
  }

  handleSocialProfileSkip = () => {
    this.moveToNextPageOrFinish(STEPS.socialProfile)
    trackInternalEvent('skip_social_sign_up')
  }

  handleIntroductionContinue = () => {
    this.props.persistSignUpInfo({ type: this.props.documentType })
    this.moveToNextPageOrFinish(STEPS.introduction)
  }

  handleContactInfoContinue = () => {
    this.moveToNextPageOrFinish(STEPS.contactInfo)
  }

  handleBackClick = currentStep => {
    const previousStep = this.stepManager.previous(currentStep)

    if (currentStep !== previousStep) {
      navigate(-1)
    }
  }

  handleIntroductionChange = event => {
    this.setState({
      introductionErrors: {
        ...this.state.introductionErrors,
        [event.target.name]: '',
      },
    })
  }

  handleContactInfoChange = event => {
    this.setState({
      contactInfoErrors: {
        ...this.state.introductionErrors,
        [event.target.name]: '',
      },
    })
  }

  checkIfAbTestExistsOnTemplates = () => {
    if (['v1', 'v2'].includes(this.singlePageSignUp)) {
      // it calculates the next step so i cant put the proper information
      this.moveToNextPageOrFinish(STEPS.templates)
      return true
    }
  }

  handleSingleSignUp = async () => {
    this.props.persistSignUpInfo({ type: this.props.documentType })
    const allOk = this.checkFirstAndLastName()
    if (!allOk) return
    this.checkEmailInformation()
  }

  checkEmailInformation = async () => {
    const { signUp, signUpInfo } = this.props
    const isEmptyEmail = !signUpInfo.email.trim().length

    if (isEmptyEmail) {
      this.setState({
        contactInfoErrors: {
          email: isEmptyEmail ? I18n.t('builder.sign_up.field_is_required') : '',
        },
      })
      this.navigateWithScope(STEPS.contactInfo)
      return
    }

    try {
      const { recaptchaToken, recaptchaAction } = await getRecaptchaLoginActionToken()

      signUp({
        type: this.props.documentType,
        isSuperApp: this.props.config?.features?.superApp,
        ...signUpInfo,
        recaptchaToken,
        recaptchaAction,
        forwardRedirectUrl: this.handleSignUpForwardRedirectUrl(),
      })
    } catch (error) {
      ErrorLogger.log(error)
    }
  }

  checkFirstAndLastName = () => {
    const { signUpInfo } = this.props
    const isEmptyFirstName = !signUpInfo.firstName.trim().length
    const isEmptyLastName = !signUpInfo.lastName.trim().length

    if (isEmptyFirstName || isEmptyLastName) {
      const errorString = I18n.t('builder.sign_up.field_is_required')
      this.setState({
        introductionErrors: {
          firstName: isEmptyFirstName ? errorString : '',
          lastName: isEmptyLastName ? errorString : '',
        },
      })
      return false
    }
    return true
  }

  moveToNextPageOrFinish = async (currentStep, navigateOptions) => {
    const nextStep = this.stepManager.next(currentStep)

    if (nextStep !== currentStep) {
      this.navigateWithScope(nextStep, navigateOptions)
    } else {
      const allOk = this.checkFirstAndLastName()

      if (!allOk) {
        this.navigateWithScope(STEPS.introduction)
        return
      }
      this.checkEmailInformation()
    }
  }

  render() {
    const { movingDirection } = this.state
    const {
      resumeTemplates,
      coverLetterTemplates,
      location,
      heroTemplateName,
      documentType,
      locale,
      resumeTemplateCategories,
      coverLetterTemplateCategories,
    } = this.props
    const { template } = this.props.signUpInfo
    const templateId = template || heroTemplateName
    const isResumeFunnel = documentType === 'resume'
    const currentStepName = this.stepManager.getStepByUrl(location.pathname)
    const categories = isResumeFunnel ? resumeTemplateCategories : coverLetterTemplateCategories
    const isGuestBuilderEnabled = isResumeFunnel && this.props.config?.features.rbpStepSignup

    const NAVBAR_STEPS = [
      I18n.t('builder.sign_up.choose_template'),
      I18n.t('builder.sign_up.enter_details'),
      isResumeFunnel
        ? I18n.t('builder.sign_up.download_resume')
        : I18n.t('builder.sign_up.download_cover_letter'),
    ]

    const templatesList = isResumeFunnel
      ? resumeTemplates
      : [
          // TODO after Greek templates release: Remove the condition
          ...(locale === 'gr'
            ? filterGreekTemplates(coverLetterTemplates, locale)
            : coverLetterTemplates),
        ]
    // locale check added to show japanese template
    const isJapaneseLocale = locale === 'ja-JP'
    let templates = templatesList
    if (!isJapaneseLocale) {
      templates = templatesList.filter(value => !JAPANESE_TEMPLATES.includes(value.id))
    } else {
      // TODO: Remove this condition after fixing ATS templates
      templates = templatesList.filter(value => !ATS_TEMPLATES.includes(value.id))
    }

    return (
      <Layout
        onClose={this.handleExitClick}
        steps={NAVBAR_STEPS}
        currentTemplate={templates.find(item => item.id === template)}
        currentStepName={currentStepName}
        currentStepNumber={this.currentNavbarIndex}
        regionPrefix={this.regionPrefix}
        isStepperDisabled={isGuestBuilderEnabled}
        isCopyrightDisabled={isGuestBuilderEnabled}
        singlePageSignUp={this.singlePageSignUp}
      >
        <RouteTransitionSwitch direction={movingDirection}>
          <Route
            path={`/${STEPS.templates}`}
            element={
              <TemplatesV2
                recommended={heroTemplateName}
                templates={templates}
                currentTemplate={template}
                onChoose={this.handleChooseTemplate}
                documentType={documentType}
                categories={categories}
                rtpAtpUnhideCtaAB={this.rtpAtpUnhideCtaAB}
                atpStickyCategories={this.atpStickyCategories === 'v1'}
              />
            }
          />

          <Route
            path={`/${STEPS.socialProfile}`}
            element={
              <SocialProfile
                onSkip={this.handleSocialProfileSkip}
                onBack={() => this.handleBackClick(STEPS.socialProfile)}
                documentType={documentType}
                templateId={templateId}
              />
            }
          />

          <Route
            path={`/${STEPS.introduction}`}
            element={
              <Introduction
                onBack={() => this.handleBackClick(STEPS.introduction)}
                onContinue={this.handleIntroductionContinue}
                firstNameError={this.state.introductionErrors.firstName}
                lastNameError={this.state.introductionErrors.lastName}
                onChange={this.handleIntroductionChange}
                documentType={this.props.documentType}
                templateId={templateId}
              />
            }
          />

          <Route
            path={`/${STEPS.contactInfo}`}
            element={
              <ContactInfo
                onBack={() => this.handleBackClick(STEPS.contactInfo)}
                onContinue={this.handleContactInfoContinue}
                documentType={documentType}
                onChange={this.handleContactInfoChange}
                outsideEmailError={this.state.contactInfoErrors.email}
                templateId={templateId}
              />
            }
          />
          <Route
            path={`/${STEPS.onePageSignUp}`}
            element={
              <SingleSignup
                onBack={() => this.handleBackClick(STEPS.contactInfo)}
                onContinue={() => this.handleSingleSignUp()}
                documentType={documentType}
                onChange={this.handleContactInfoChange}
                outsideEmailError={this.state.contactInfoErrors.email}
                templateId={templateId}
                firstNameError={this.state.introductionErrors.firstName}
                lastNameError={this.state.introductionErrors.lastName}
              />
            }
          />
          <Route
            path="*"
            element={
              <TemplatesV2
                recommended={heroTemplateName}
                templates={templates}
                currentTemplate={template}
                onChoose={this.handleChooseTemplate}
                documentType={documentType}
                categories={categories}
                rtpAtpUnhideCtaAB={this.rtpAtpUnhideCtaAB}
                atpStickyCategories={this.atpStickyCategories === 'v1'}
              />
            }
          />
        </RouteTransitionSwitch>

        <Routes>
          {this.state.componentWasMounted && (
            <Route path="" element={<Navigate replace to="templates" />} />
          )}
        </Routes>
        <Illustrations currentStepName={currentStepName} />
        <CookieV4 />
      </Layout>
    )
  }
}

// ---
// Connect to data
// ---
const mapStateToProps = state => ({
  config: appSelectors.config(state, ConfigScopesEnum.signUp),
  resumeTemplates: state.signUp.resumeTemplates,
  coverLetterTemplates: state.signUp.coverLetterTemplates,
  resumeTemplateCategories: state.signUp.resumeTemplateCategories,
  coverLetterTemplateCategories: state.signUp.coverLetterTemplateCategories,
  heroTemplateName: state.signUp.heroTemplateName,
  signUpInfo: state.signUp.signUpInfo,
  locale: state.application.configs.country.locale,
})

const mapDispatchToProps = {
  signUp: actions.signUpRequest,
  persistSignUpInfo: actions.persistSignUpInfo,
  loadSignUpInfo: actions.loadSignUpInfo,
  getSignUpPayload: actions.getSignUpPayload,
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(SignUpView))
